import { useEffect, useRef, useState, useCallback } from 'react';
import DOMPurify from 'dompurify';
import api from '../lib/api';
import RichTextEditor from './RichTextEditor';
import { Button } from './ui/button';
import { Loader2, Send, Paperclip, X, FileText, MessageSquare, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

/** ProductionThread — per-production messaging between the scientific
 *  committee (admin) and the reader who submitted it. Both sides can send
 *  rich-text messages and optionally attach a file / image. Admin notes
 *  are rendered above the thread with all their original formatting.
 *
 *  Props:
 *    productionId  — required
 *    viewerType    — 'admin' | 'reader'
 *    adminNotes    — HTML string of the committee's decision notes (optional)
 *    adminAttachmentUrl / adminAttachmentName — optional file the committee
 *                    attached with their review decision
 */
const ProductionThread = ({
  productionId,
  viewerType,
  adminNotes,
  adminAttachmentUrl,
  adminAttachmentName,
}) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [attachment, setAttachment] = useState(null);   // { url, name, type }
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const listEndRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/productions/${productionId}/messages`);
      setMessages(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل تحميل المحادثة');
    } finally {
      setLoading(false);
    }
  }, [productionId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Auto-scroll to the newest message when it arrives.
    listEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length]);

  const handleAttach = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // Route by mime — /upload/image for images, /upload/document for the rest
      const isImage = /^image\//.test(file.type);
      const endpoint = isImage ? '/upload/image' : '/upload/document';
      const res = await api.post(endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${res.data.path}`;
      setAttachment({ url, name: res.data.filename || file.name, isImage });
      toast.success('تم رفع المرفق');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل رفع المرفق');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const isEmptyContent = (html) => {
    if (!html) return true;
    // Strip tags to check for real characters (Tiptap sends "<p></p>" when empty).
    return html.replace(/<[^>]*>/g, '').replace(/\s|&nbsp;/g, '').length === 0;
  };

  const send = async () => {
    if (isEmptyContent(draft) && !attachment) {
      toast.error('اكتب رسالة أو أرفق ملفاً');
      return;
    }
    setSending(true);
    try {
      const payload = {
        content: isEmptyContent(draft) ? '' : draft,
        attachment_url: attachment?.url || '',
        attachment_name: attachment?.name || '',
      };
      const res = await api.post(`/productions/${productionId}/messages`, payload);
      setMessages(prev => [...prev, res.data]);
      setDraft('');
      setAttachment(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل الإرسال');
    } finally {
      setSending(false);
    }
  };

  const isImageAttachment = (url, name) => {
    const s = `${url || ''} ${name || ''}`.toLowerCase();
    return /\.(png|jpe?g|gif|webp|svg)(\?|$)/.test(s);
  };

  return (
    <div className="space-y-4" data-testid="production-thread">
      {/* Admin decision notes (rich HTML, preserved formatting) */}
      {(adminNotes || adminAttachmentUrl) && (
        <div className="rounded-xl border-2 border-amber-300 bg-amber-50/70 p-4" data-testid="production-admin-notes">
          <p className="text-xs font-bold text-amber-800 mb-2">ملاحظات اللجنة العلمية</p>
          {adminNotes && (
            <div
              className="prose prose-sm max-w-none text-amber-900 answer-html"
              dir="rtl"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(adminNotes) }}
            />
          )}
          {adminAttachmentUrl && (
            <a
              href={adminAttachmentUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-amber-800 border border-amber-300 rounded-lg px-3 py-1.5 bg-white hover:bg-amber-100 transition-colors"
              data-testid="production-admin-attachment"
            >
              <FileText className="w-4 h-4" />
              {adminAttachmentName || 'ملف مرفق من الإدارة'}
            </a>
          )}
        </div>
      )}

      {/* Thread header */}
      <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
        <MessageSquare className="w-4 h-4 text-primary" />
        <h4 className="text-sm font-bold text-accent">المحادثة مع {viewerType === 'admin' ? 'القارئ' : 'اللجنة العلمية'}</h4>
      </div>

      {/* Messages list */}
      <div className="space-y-3 max-h-[45vh] overflow-y-auto pr-1" data-testid="production-messages">
        {loading ? (
          <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
        ) : messages.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-6">لا توجد رسائل بعد — كن أول من يكتب.</p>
        ) : (
          messages.map((m) => {
            const mine = m.sender_type === viewerType;
            const bubble = mine
              ? 'bg-primary/10 border-primary/25 text-accent self-end'
              : 'bg-gray-50 border-gray-200 text-gray-800 self-start';
            const isImg = m.attachment_url && isImageAttachment(m.attachment_url, m.attachment_name);
            return (
              <div key={m.id} className={`flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
                <div className={`max-w-[85%] border rounded-2xl p-3 ${bubble}`} data-testid={`prod-msg-${m.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[11px] font-bold text-primary/80">{m.sender_name || (m.sender_type === 'admin' ? 'اللجنة العلمية' : 'القارئ')}</span>
                    <span className="text-[10px] text-muted-foreground">
                      {m.created_at ? new Date(m.created_at).toLocaleString('ar', { dateStyle: 'short', timeStyle: 'short' }) : ''}
                    </span>
                  </div>
                  {m.content && (
                    <div
                      className="prose prose-sm max-w-none answer-html leading-relaxed"
                      dir="rtl"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(m.content) }}
                    />
                  )}
                  {m.attachment_url && (
                    isImg ? (
                      <a href={m.attachment_url} target="_blank" rel="noreferrer" className="block mt-2">
                        <img src={m.attachment_url} alt={m.attachment_name || ''} className="max-h-64 rounded-lg border" />
                      </a>
                    ) : (
                      <a
                        href={m.attachment_url}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-2 inline-flex items-center gap-2 text-xs font-semibold border rounded-lg px-2.5 py-1.5 bg-white hover:bg-gray-100 transition-colors"
                      >
                        <FileText className="w-3.5 h-3.5" />
                        {m.attachment_name || 'ملف مرفق'}
                      </a>
                    )
                  )}
                </div>
              </div>
            );
          })
        )}
        <div ref={listEndRef} />
      </div>

      {/* Composer */}
      <div className="border-2 border-gray-200 rounded-xl p-2 bg-white space-y-2" data-testid="production-composer">
        <RichTextEditor value={draft} onChange={setDraft} />
        {attachment && (
          <div className="flex items-center justify-between text-xs bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
            <span className="flex items-center gap-2 text-emerald-800">
              {attachment.isImage ? <ImageIcon className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
              {attachment.name}
            </span>
            <button
              type="button"
              onClick={() => setAttachment(null)}
              className="text-red-500 hover:bg-red-50 rounded-full p-1"
              data-testid="production-attach-remove"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        <div className="flex items-center gap-2 justify-between">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="production-attach-btn"
              title="إرفاق صورة أو ملف"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
              <span className="mr-1">إرفاق</span>
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp"
              className="hidden"
              onChange={handleAttach}
            />
          </div>
          <Button
            type="button"
            onClick={send}
            disabled={sending || uploading}
            className="bg-primary hover:bg-primary/90"
            data-testid="production-send-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin ml-1" /> : <Send className="w-4 h-4 ml-1" />}
            إرسال
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProductionThread;
